/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICommerceApiSettings,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { getCartState } from '@msdyn365-commerce/global-state';
import {
    CartLine,
    ChannelDeliveryOptionConfiguration,
    ProductWarehouse,
    InventoryAvailabilitySearchCriteria,
    ProductAvailableQuantity
    // ProductAvailableQuantity
} from '@msdyn365-commerce/retail-proxy';
import { getOrgUnitConfigurationAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/OrgUnitsDataActions.g';
import {
    getEstimatedAvailabilityAsync,
    getEstimatedProductWarehouseAvailabilityAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { getChannelDeliveryOptionConfigurationAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/StoreOperationsDataActions.g';

import {
    ActiveCartProductsInput,
    ArrayExtensions,
    buildCacheKey,
    createInventoryAvailabilitySearchCriteria,
    getActiveCartProductsAction,
    InventoryLevels,
    IProductInventoryInformation,
    mapAggregatedProductInventoryInformation,
    mapProductInventoryInformation
} from '@msdyn365-commerce-modules/retail-actions';
// import getFeatureStateActionDataAction, { FeatureStateInput } from './mil-get-feature-state.action';
// import { ProductInventoryAvailabilityClass } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceModels.g';
// import { getCustomerWarehousesAsync } from './DataActionExtension.g';
import { createGetFeatureStateInput, getFeatureState } from '@msdyn365-commerce-modules/retail-actions/';

/**
 * Input class for availabilities for items in cart.
 */
export class ProductAvailabilitiesForCartLineItems implements IActionInput {
    private readonly apiSettings: ICommerceApiSettings;

    constructor(apiSettings: ICommerceApiSettings) {
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => buildCacheKey('ActiveCartLineItemsAvailability', this.apiSettings);

    public getCacheObjectType = () => 'ActiveCartLineItemsAvailability';

    public dataCacheType = (): CacheType => 'none';
}

const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>) => {
    return new ProductAvailabilitiesForCartLineItems(inputData.requestContext.apiSettings);
};

/**
 * Calls the Retail Feature State API and returns a list of feature with isEnabled flag.
 * @param cartLine
 * @param featureSate
 * @param channelDeliveryOptionConfig
 * @param pickupDeliveryMode
 */
const getDeliveryMode = (
    cartLine: CartLine,
    featureSate: boolean = false,
    channelDeliveryOptionConfig: ChannelDeliveryOptionConfiguration,
    pickupDeliveryMode?: string
) => {
    if (!featureSate) {
        return cartLine.DeliveryMode === pickupDeliveryMode;
    }
    return (
        cartLine.DeliveryMode ===
        channelDeliveryOptionConfig?.PickupDeliveryModeCodes?.find(deliveryMode => deliveryMode === cartLine.DeliveryMode)
    );
};

/**
 * Calls the Retail API to get the product availabilities for items in the cart.
 * @param input
 * @param ctx
 */
// eslint-disable-next-line complexity
export async function getAvailabilitiesForCartLineItems(
    input: ProductAvailabilitiesForCartLineItems,
    ctx: IActionContext
): Promise<IProductInventoryInformation[]> {
    // If no input is provided fail out
    if (!input) {
        throw new Error('[getAvailabilitiesForCartLineItems]No valid Input was provided, failing');
    }

    const shippingItems: CartLine[] = [];
    const bopisItems = [];
    let productAvailabilities: IProductInventoryInformation[] = [];
    let cartAvailabilites: IProductInventoryInformation[] = []; // total availabilites
    const multiplePickupStoreSwitchName = 'Dynamics.AX.Application.RetailMultiplePickupDeliveryModeFeature';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let channelDeliveryOptionConfig: any;

    const cartState = await getCartState(ctx);
    const cart = cartState.cart;
    const channelConfiguration = await getOrgUnitConfigurationAsync({ callerContext: ctx });
    const products = await getActiveCartProductsAction(new ActiveCartProductsInput(), ctx);

    /**
     * Calls the Retail Feature State API and returns a list of feature with isEnabled flag.
     * @param context
     */
    // async function getFeatureState(context: IActionContext): Promise<FeatureState[]> {
    //     return getFeatureStateActionDataAction(new FeatureStateInput(), context);
    // }
    const featureState = await getFeatureState(createGetFeatureStateInput(ctx), ctx);
    const retailMultiplePickUpOptionEnabled = featureState?.find(item => item.Name === multiplePickupStoreSwitchName)?.IsEnabled;
    if (retailMultiplePickUpOptionEnabled) {
        channelDeliveryOptionConfig = await getChannelDeliveryOptionConfigurationAsync({ callerContext: ctx });
    }
    const PickupDeliveryModeCode = channelConfiguration.PickupDeliveryModeCode;
    const EmailDeliveryModeCode = channelConfiguration.EmailDeliveryModeCode;

    if (!cart || !channelConfiguration || !products || products.length === 0) {
        ctx.trace('[getAvailabilitiesForCartLineItems] Not able to get cart OR channelConfiguration or no products in cart');
        return <IProductInventoryInformation[]>[];
    }

    if (cart && cart.Id && cart.CartLines && cart.CartLines.length > 0 && channelConfiguration) {
        for (const cartLine of cart.CartLines) {
            if (
                cartLine.DeliveryMode &&
                cartLine.DeliveryMode !== '' &&
                getDeliveryMode(cartLine, retailMultiplePickUpOptionEnabled, channelDeliveryOptionConfig, PickupDeliveryModeCode)
            ) {
                bopisItems.push(cartLine);
            } else if (EmailDeliveryModeCode === '' || cartLine.DeliveryMode !== EmailDeliveryModeCode) {
                shippingItems.push(cartLine);
            }
        }
    }

    if (shippingItems && shippingItems.length > 0) {
        let productIds = shippingItems.map(x => x.ProductId!);
        productIds = ArrayExtensions.unique(productIds);
        const searchCriteria = createInventoryAvailabilitySearchCriteria(ctx, productIds, true);
        const shippingProductAvailabilities = await getEstimatedAvailabilityAsync(
            { callerContext: ctx, bypassCache: 'get' },
            searchCriteria
        );

        const inventSearch: InventoryAvailabilitySearchCriteria = {
            ProductIds: productIds,
            DefaultWarehouseOnly: false,
            FilterByChannelFulfillmentGroup: true
        };

        const availabilities = await getEstimatedAvailabilityAsync({ callerContext: ctx, bypassCache: 'get' }, inventSearch);

        if (
            shippingProductAvailabilities &&
            shippingProductAvailabilities.ProductWarehouseInventoryAvailabilities &&
            shippingProductAvailabilities.ProductWarehouseInventoryAvailabilities.length > 0
        ) {
            const inventoryMethod = ctx.requestContext.app.config.inventoryLevel;
            productAvailabilities = mapAggregatedProductInventoryInformation(ctx, shippingProductAvailabilities);
            const aggProductInvAvail = availabilities.AggregatedProductInventoryAvailabilities || [];
            // create product Quantity
            aggProductInvAvail.forEach(product => {
                const productAvailQuant: ProductAvailableQuantity = {
                    ProductId: product.ProductId,
                    AvailableQuantity:
                        inventoryMethod === InventoryLevels.physicalAvailable
                            ? product.PhysicalAvailableQuantity
                            : product.TotalAvailableQuantity,
                    UnitOfMeasure: product.UnitOfMeasure,
                    ExtensionProperties: product.ExtensionProperties
                };
                const productInventoryInfo: IProductInventoryInformation = {
                    ProductAvailableQuantity: productAvailQuant,
                    StockLevelCode:
                        inventoryMethod === InventoryLevels.physicalAvailable
                            ? product.PhysicalAvailableInventoryLevelCode
                            : product.TotalAvailableInventoryLevelCode,
                    StockLevelLabel:
                        inventoryMethod === InventoryLevels.physicalAvailable
                            ? product.PhysicalAvailableInventoryLevelLabel
                            : product.TotalAvailableInventoryLevelLabel,
                    IsProductAvailable: productAvailQuant.AvailableQuantity ? productAvailQuant.AvailableQuantity > 0 : false
                };
                cartAvailabilites.unshift(productInventoryInfo);
            });
        }
    }

    if (bopisItems && bopisItems.length > 0) {
        const productWarehouses: ProductWarehouse[] = [];

        for (const bopisItem of bopisItems) {
            const productWarehouse: ProductWarehouse = {
                ProductId: bopisItem.ProductId,
                InventLocationId: bopisItem.WarehouseId
            };

            if (ctx.requestContext.channel && ctx.requestContext.channel.InventLocationDataAreaId) {
                productWarehouse.DataAreaId = ctx.requestContext.channel.InventLocationDataAreaId;
            }
            productWarehouses.push(productWarehouse);
        }
        const quantityUnitTypeSales: number = 2;
        const getProductWarehouseAvail = await getEstimatedProductWarehouseAvailabilityAsync(
            { callerContext: ctx, bypassCache: 'get', queryResultSettings: {} },
            productWarehouses,
            quantityUnitTypeSales
        );

        if (
            getProductWarehouseAvail &&
            getProductWarehouseAvail.ProductWarehouseInventoryAvailabilities &&
            getProductWarehouseAvail.ProductWarehouseInventoryAvailabilities.length > 0
        ) {
            const productWarehouseMapping = mapProductInventoryInformation(
                ctx,
                getProductWarehouseAvail?.ProductWarehouseInventoryAvailabilities
            );
            if (productWarehouseMapping && productWarehouseMapping.length > 0) {
                for (const item of productWarehouseMapping) {
                    productAvailabilities.push(item);
                }
            }
        }
    }
    if (cartAvailabilites && cartAvailabilites.length > 0) {
        return cartAvailabilites;
    }

    if (productAvailabilities && productAvailabilities.length > 0) {
        return productAvailabilities;
    }

    ctx.trace('[getAvailabilitiesForCartLineItems] unable to get availabilities for product');
    return <IProductInventoryInformation[]>[];
}

export const getAvailabilitiesForCartLineItemsDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-availabilities-cartlines',
    action: <IAction<IProductInventoryInformation[]>>getAvailabilitiesForCartLineItems,
    input: createInput
});

export default getAvailabilitiesForCartLineItemsDataAction;
